/**
 * Global Excel Configurator - Adapter Registry
 *
 * Manages registration and retrieval of data source adapters.
 */

import type { DataSourceAdapter } from "../types";

// ============================================================================
// Registry
// ============================================================================

/**
 * Global registry for data source adapters
 */
class AdapterRegistry {
  private adapters: Map<string, DataSourceAdapter> = new Map();

  /**
   * Register a data source adapter
   *
   * @param adapter - Adapter to register
   * @throws Error if adapter type is already registered
   */
  register(adapter: DataSourceAdapter): void {
    if (this.adapters.has(adapter.type)) {
      throw new Error(`Adapter for type "${adapter.type}" is already registered`);
    }

    this.adapters.set(adapter.type, adapter);
    console.debug(`[AdapterRegistry] Registered adapter: ${adapter.type} (${adapter.name})`);
  }

  /**
   * Unregister a data source adapter
   *
   * @param type - Adapter type to unregister
   */
  unregister(type: string): void {
    if (this.adapters.delete(type)) {
      console.debug(`[AdapterRegistry] Unregistered adapter: ${type}`);
    }
  }

  /**
   * Get a registered adapter by type
   *
   * @param type - Adapter type
   * @returns Adapter instance
   * @throws Error if adapter not found
   */
  getAdapter<TAdapter extends DataSourceAdapter = DataSourceAdapter>(
    type: string
  ): TAdapter {
    const adapter = this.adapters.get(type);

    if (!adapter) {
      throw new Error(
        `No adapter registered for type "${type}". Available types: ${this.getAvailableTypes().join(", ")}`
      );
    }

    return adapter as TAdapter;
  }

  /**
   * Check if an adapter is registered
   *
   * @param type - Adapter type
   * @returns True if registered
   */
  hasAdapter(type: string): boolean {
    return this.adapters.has(type);
  }

  /**
   * Get all registered adapter types
   *
   * @returns Array of adapter types
   */
  getAvailableTypes(): string[] {
    return Array.from(this.adapters.keys());
  }

  /**
   * Get all registered adapters
   *
   * @returns Array of adapters
   */
  getAllAdapters(): DataSourceAdapter[] {
    return Array.from(this.adapters.values());
  }

  /**
   * Clear all registered adapters
   */
  clear(): void {
    this.adapters.clear();
    console.debug("[AdapterRegistry] Cleared all adapters");
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

/**
 * Global adapter registry instance
 */
const adapterRegistry = new AdapterRegistry();

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Register a data source adapter (convenience function)
 *
 * @param adapter - Adapter to register
 */
export function registerAdapter(adapter: DataSourceAdapter): void {
  adapterRegistry.register(adapter);
}

/**
 * Get a registered adapter (convenience function)
 *
 * @param type - Adapter type
 * @returns Adapter instance
 */
export function getAdapter<TAdapter extends DataSourceAdapter = DataSourceAdapter>(
  type: string
): TAdapter {
  return adapterRegistry.getAdapter<TAdapter>(type);
}

